import { useEffect, useState } from 'react';
import { Box, Button, IconButton, Skeleton, Tooltip, Typography } from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AddIcon from '@mui/icons-material/Add';

import { pageActionsFor } from 'helpers/pageActions';
import useAuthStore from '../../stores/useAuthStore';
import useWalletStore, { useOwnWallet } from '../../stores/useWalletStore';
import TopUpModal from '../../pages/platform/TopUpModal';
import { palette, typography, size, radius } from 'theme/tokens';

/**
 * The partner's wallet balance in the header, with a way to add to it.
 *
 * ── Why it lives here rather than only on a page ───────────────────────────
 * The balance is the number that decides whether the next booking can be paid
 * for, so it belongs where it is always visible — the same place Razorpay,
 * Twilio and AWS put theirs. The Wallet page is route-only with no sidebar
 * entry, which made "add money" effectively unreachable.
 *
 * ── The permission gate comes from the ROUTE, not a string here ────────────
 * `pageActionsFor` is handed the wallet page's own path, so this control is
 * gated by the SAME navPermissions entry that decides whether that page opens.
 * Naming the module inline ('crm', 'Finance & Settlement') would work today and
 * fail silently the day the mapping changes — see the warning at the top of
 * helpers/pageActions.js. The path is the single source of truth.
 *
 * Denied roles see the balance and no button: reading what you have is not the
 * same permission as adding to it, and hiding the figure would leave a partner
 * unable to see why a booking was refused.
 *
 * ── ⚠️ NOT EVERY PARTNER CAN TOP UP ───────────────────────────────────────
 * The BALANCE is shown in whatever currency the session trades in — USD, QAR,
 * INR. TOPPING UP is INR-only, because Razorpay settles INR and the booking
 * engine refuses anything else. A USD partner therefore sees their real
 * balance and no add control; offering it would create an INR wallet they
 * cannot spend from and leave their actual money untouched.
 */

/** Must match the entry in constants/navPermissions.js and partner routes. */
const WALLET_PATH = '/partnerAdmin/platform/wallet';

/**
 * A glance figure: no decimals, grouped for the currency's own locale.
 *
 * en-IN for rupees so ₹1,00,000 groups the way an Indian partner reads it,
 * en-US otherwise. The ISO code rather than a symbol — QAR has no widely
 * recognised one, and a bare number beside a symbol nobody recognises is worse
 * than a code everybody can look up. Decimals are dropped deliberately: this is
 * a header glance, and the wallet page carries the exact figure.
 */
const formatBalance = (n, code) => {
  const amount = Number(n || 0);
  if (code === 'INR') return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  return `${code || ''} ${amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}`.trim();
};

/**
 * `inMenu` renders it as a row of the header's profile menu instead of a
 * chip in the bar.
 */
export default function WalletBalanceChip({ compact = false, inMenu = false }) {
  const perms = useAuthStore((s) => s.perms);
  // Never s.wallet directly — see the note on stamping in the store.
  const wallet = useOwnWallet();
  const loaded = useWalletStore((s) => s.loaded);
  const currency = useWalletStore((s) => s.currency);
  const canTopUp = useWalletStore((s) => s.canTopUp);
  const load = useWalletStore((s) => s.load);
  const refresh = useWalletStore((s) => s.refresh);

  const [topUpOpen, setTopUpOpen] = useState(false);

  useEffect(() => {
    load();
  }, [load]);

  const actions = pageActionsFor(perms, WALLET_PATH, 'partner');
  const balance = wallet?.balances?.balance;

  // Nothing to show until the first attempt settles. A wallet that does not
  // exist yet reads as 0 rather than an error: a partner who has never topped
  // up has no row until their first order creates one, which is ordinary.
  if (!loaded && inMenu) {
    return <Skeleton variant="rounded" height={size[48]} sx={{ mx: 1, bgcolor: palette.alpha.black[6] }} />;
  }

  if (inMenu) {
    return (
      <>
        <Box sx={{ mx: 1, px: 1, py: 1, display: 'flex', alignItems: 'center', gap: 1.5, borderRadius: `${radius.md}px`, backgroundColor: palette.alpha.black[4] }}>
          <Box sx={{ width: size[32], height: size[32], flexShrink: 0, borderRadius: `${radius.md}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: palette.white }}>
            <AccountBalanceWalletOutlinedIcon sx={{ fontSize: typography.fontSize[18], color: 'primary.main' }} />
          </Box>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            {/* <Typography sx={{ fontSize: typography.fontSize[12], color: palette.tw.gray[500] }}>Wallet balance</Typography> */}
            <Typography sx={{ fontSize: typography.fontSize[12], color: palette.tw.gray[500] }}>Available Funds</Typography>
            <Typography sx={{ fontSize: typography.fontSize[14], fontWeight: typography.fontWeight.semiBold, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
              {formatBalance(balance, currency)}
            </Typography>
          </Box>
          {actions.canCreate && canTopUp && (
            <Button
              size="small"
              variant="contained"
              disableElevation
              aria-label="Add money to wallet"
              startIcon={<AddIcon sx={{ fontSize: `${typography.fontSize[16]} !important` }} />}
              onClick={() => setTopUpOpen(true)}
              sx={{
                flexShrink: 0,
                minWidth: 0,
                height: size[28],
                px: 1.25,
                borderRadius: `${radius.md}px`,
                fontSize: typography.fontSize[12],
                fontWeight: typography.fontWeight.semiBold,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                '& .MuiButton-startIcon': { mr: 0.5, ml: 0 },
              }}
            >
              Add
            </Button>
          )}
        </Box>
        <TopUpModal open={topUpOpen} onClose={() => setTopUpOpen(false)} onCredited={refresh} />
      </>
    );
  }

  if (!loaded) {
    return (
      <Skeleton
        variant="rounded"
        width={compact ? 64 : 96}
        height={size[26]}
        sx={{ flexShrink: 0, bgcolor: palette.alpha.black[6] }}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 0.25 : 0.5,
          pl: compact ? 0.75 : 1.25,
          pr: canTopUp && actions.canCreate ? 0.25 : (compact ? 0.75 : 1.25),
          py: 0.25,
          borderRadius: 1,
          backgroundColor: palette.alpha.black[4],
          flexShrink: 0,
        }}
      >
        {/* Same label for every currency (SMB-4329): a non-INR partner just sees their
            balance — the missing "+" is enough, no INR-only explanation. */}
        {/* <Tooltip title="Wallet balance"> */}
          <Tooltip title="Available Funds">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
            <AccountBalanceWalletOutlinedIcon sx={{ fontSize: typography.fontSize[16], color: palette.neutral[470] }} />
            {/*
              ⚠️ EXPLICIT COLOUR, not inherited.

              MUI's AppBar defaults to color="primary", so its text colour is the
              CONTRAST text for the primary — white. PartnerHeader overrides the
              AppBar's background to #FFFFFF but not its text colour, which is why
              every other label in that bar names its own (#AAAAAA, #555555,
              text.primary). Inheriting here put a near-white balance on a white
              bar: present in the DOM, invisible on screen.
            */}
            <Typography
              variant="body2"
              sx={{
                fontWeight: typography.fontWeight.semiBold,
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
                color: 'text.primary',
              }}
            >
              {formatBalance(balance, currency)}
            </Typography>
          </Box>
        </Tooltip>

        {actions.canCreate && canTopUp && (
          <Tooltip title="Add money">
            <IconButton
              size="small"
              aria-label="Add money to wallet"
              onClick={() => setTopUpOpen(true)}
              sx={{ width: size[24], height: size[24], color: 'primary.main' }}
            >
              <AddIcon sx={{ fontSize: typography.fontSize[15] }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <TopUpModal open={topUpOpen} onClose={() => setTopUpOpen(false)} onCredited={refresh} />
    </>
  );
}
